// import Vue from "vue";

import { resetState } from "./helpers";
import { api } from "@/services/api";

import { PARTICIPANT } from "@/config/session";


const initState = () => ({
  sessionID: null,
  participantID: null, // set in various ways depending on how a user joins
  role: null,
  participants: null,

  // core session object data
  session: {},

  // basic session existence state
  status: {
    creating: false,
    created: false,
    began: false, // did both sides initially connect?
    ended: false,
    ending: false,
    endedLocally: false,
    endedRemotely: false
  },

  // array of local connection timer start/top events
  connectionTimer: [],

  // status of the session timer, contains a log of start/stop/reset events
  sessionTimer: {
    running: false,
    // list of events, each item in format of `{action: "start|stop|reset", time: Date().getTime()}`
    events: []
  },

  // mapping of all participants in the session by ID
  participantMap: {},

  // user info of the other person, either the linguist, or customer
  remoteUser: {},

  // incoming invite
  inviteID: null,
  invite: {},
  isIncomingCallActive: false,

  //zoom specific info
  zoomMeetingInfo: {}, //contains information about current zoom meeting including meeting settings and meeting type

  //linguist status information for current session
  linguistStatusInformation: {}
});

const getters = {
  baseURL(state) {
    return `/v1/sessions/${state.sessionID}`;
  },
  isLinguist(state) {
    return state.role === PARTICIPANT.ROLE.LINGUIST;
  },
  localParticipant(state) {
    for (const p of Object.values(state.participantMap)) {
      if (p.id === state.participantID) {
        return p;
      }
    }
    return null;
  },
  linguistParticipant(state) {
    for (const p of Object.values(state.participantMap)) {
      if (p.role === PARTICIPANT.ROLE.LINGUIST) {
        return p;
      }
    }
    return null;
  },
};

const mutations = {
  reset(state) {
    Object.assign(state, initState());
  },
  startCreatingSession(state, data) {
    resetState(state, initState());
    state.status.creating = true;
    state.role = PARTICIPANT.ROLE.CREATOR;
    state.session = data;
  },
  sessionCreated(state, data) {
    // TODO: local storage to save/restore session in progress
    state.status.creating = false;
    state.status.created = true;
    state.sessionID = data.sessionID;
    state.session = data;
    state.participantID = data.participant.id;
    state.participantMap = {};
    state.participantMap[state.participantID] = data.participant;
  },
  sessionCreateOrJoinFailed(state) {
    resetState(state, initState());
  },
  sessionEndingLocally(state) {
    state.status.ending = true;
  },
  sessionEndedLocally(state, data = null) {
    state.status.ending = false;
    state.status.endedRemotely = false;
    state.status.ended = true;
    state.status.endedLocally = true;
    if (data) {
      state.session.endReason = data.reason;
    }
  },
  sessionEndedRemotely(state, data = null) {
    state.status.ended = true;
    state.status.endedRemotely = true;
    state.status.ending = false;
    state.status.endedLocally = false;
    if (data) {
      state.session.endReason = data.reason;
    }
  },
  setParticipants(state, data) {
    state.participants=data
    // update each key we have - ensure we set each key individually to avoid
    // overwriting more recent information
    // for (const p of data) {
    //   // if (!state.participantMap[p.id]) {
    //   //   Vue.set(state.participantMap, p.id, createEmptyParticipant());
    //   // }
    //   Object.keys(p).forEach(key => {
    //     state.participantMap[p.id][key] = p[key];
    //   });
    // }
  },
  setRemoteUser(state, user) {
    state.remoteUser = user;
  },
  setZoomMeetingInfo(state, info){
    Object.assign(state.zoomMeetingInfo, info)
  },
  setLinguistStatus(state, status){
    //TODO: Check if this works after API updates in GPS-1089
    Object.assign(state.linguistStatusInformation, status)
  },
  setSessionID(state, val) {
    state.sessionID = val;
  }
};

const actions = {
  // create and join session as the creator
  createNewSession({ state, dispatch, commit }, data) {
    commit("startCreatingSession", data);

    return new Promise((resolve, reject) => {
      let createRes = null;
      api
        .post("/v1/sessions", data)
        .then(res => {
          createRes = res;
          commit("sessionCreated", res.data);
        })
        .then(() => {
          return Promise.all([
            dispatch("ratings/getRatingTags", { sessionID: state.sessionID}, { root: true}), //get rating tags for newly created session
            dispatch("loadParticipants"),
          ])
        })
        .then(() => {
          resolve(createRes);
        })
        .catch(e => {
          commit("sessionCreateOrJoinFailed", e);
          reject(e);
        });
    });
  },
  // sets the remote user, generally set when a linguist accepts an incoming call
  setRemoteUser({ dispatch, commit }, user) {
    commit("setRemoteUser", user);
    dispatch("loadParticipants");
  },
  loadParticipants({ commit, dispatch, getters, state }) {
    // TODO:  automatically check for new participants and load them separately
    let res = null;
    return new Promise((resolve, reject) => {
      api
        .get(`${getters.baseURL}/participants`)
        .then(r => {
          res = r;
          // first check if we have any new participants, we'll also
          // do another request to load more information about them
          let newIDs = [];
          for (let p of res.data) {
            if (!state.participantMap[p.id]) {
              newIDs.push(p.id);
            }
          }
          commit("setParticipants", res.data);
          return newIDs;
        })
        .then((newIDs = []) => {
          let promises = [];
          for (const id of newIDs) {
            promises.push(dispatch("loadParticipant", { participantID: id }));
          }
          return Promise.all(promises);
        })
        .then(() => {
          // check for local participant having been removed
          if (getters.localParticipant && getters.localParticipant.removed) {
            commit("sessionEndedRemotely");
          }

          resolve(res.data);
        })
        .catch(reject);
    });
  },
  loadParticipant({ getters, commit }, data) {
    const { participantID } = data;
    return new Promise((resolve, reject) => {
      api
        .get(`${getters.baseURL}/participants/${participantID}`)
        .then(res => {
          // check for local participant having been removed
          if (getters.localParticipant && getters.localParticipant.removed) {
            commit("sessionEndedRemotely");
          }

          resolve(res.data);
        })
        .catch(reject);
    });
  },
  endSessionLocally({ commit, getters}, data) {
    commit("sessionEndingLocally");
    return new Promise((resolve, reject) => {
      api
        .put(`${getters.baseURL}/end`, {
          reason: data.reason,
          // timer: 3000
          // @todo take timer from zoom
        })
        .then(res => {
          // don't do anything here because ending the session successfully will result
          // in receiving a signal from the server that the session was ended
          resolve(res);
        })
        .catch(err => {
          // but if the attempt to end the session failed - we'll
          // still end it locally.  It could have failed because of a
          // local connection failure
          commit("sessionEndedLocally", data);
          reject(err);
        });
    });
  },
  //for creating a new zoom call from within the jeenie app
  createZoomMeeting(){
    return new Promise((resolve, reject)=>{
      api.post('/v1/integrations/zoom/scheduled-meeting')
      .then((res)=>{
        resolve(res.data)
      })
      .catch((e)=>{
        reject(e)
      })
    })
  },
  //for getting information about current zoom meeting
  getZoomMeetingInfo({ commit }, meetingID){
    return new Promise((resolve, reject)=>{
      api.get(`/v1/integrations/zoom/meeting/${meetingID}`)
        .then((res)=>{
          commit('setZoomMeetingInfo', res.data)
          resolve(res)
        })
        .catch((e)=>{reject(e)})
    })
  },
  //update linguist's status in the current session
  updateLinguistStatus({ commit }, data ={}){
    let {latestRecord , connectionType, servicePlatformMeta } = data
    let now = new Date().toISOString()
    let {sessionID , id} = latestRecord
    const payload = {
      "source": "solo_api",
      "type": connectionType,
      "time": now,
      "servicePlatformMeta": servicePlatformMeta
  }
    return new Promise((resolve, reject)=>{
      api.post(`/v1/sessions/${sessionID}/participants/${id}/events`, payload)
        .then((res)=>{
          commit('setLinguistStatus', res.data)
          resolve(res.data)
        })
        .catch(reject)
    })
  },
  //update linguist's participants in the current session
  updateLinguistParticipants({ dispatch }, data = {}) {
    let { latestRecord, servicePlatformMeta } = data
    let { sessionID, id } = latestRecord
    const payload = {
      "servicePlatformMeta": servicePlatformMeta
    }
    return new Promise((resolve, reject) => {
      api.patch(`/v1/sessions/${sessionID}/participants/${id}`, payload)
        .then((res) => {
          dispatch('updateLinguistStatus', data)
          resolve(res.data)
        })
        .catch(reject)
    })
  },
  setSessionID({ commit }, sessionID) {
    commit("setSessionID", sessionID);
  },
  // eslint-disable-next-line
  getSessionStatus({  }, data) {
    return new Promise((resolve, reject) => {
        api
            .get(`/v1/sessions/${data.sessionID}/status`)
            .then(res => {
                resolve(res.data);
            })
            .catch(reject)
    })
  }
};

export const createNewModule = () => ({
  state: initState,
  getters,
  mutations,
  actions
});

export default {
  namespaced: true,
  state: initState,
  getters,
  mutations,
  actions
};
